import { initEccLib, networks, Psbt } from 'bitcoinjs-lib'
import { fromHex } from '@dfinity/agent'
import { logger } from '../logger'
import { fetchEnv } from '../utils/fetchEnv'
import type { UTXO } from '../interfaces/walletActions'
import { BTCNFT } from '../interfaces/common'
import * as ecc from '@bitcoin-js/tiny-secp256k1-asmjs'
initEccLib(ecc)

const BASE_URL = 'https://api.bitcore.io/api/BTC'
const CHAIN_PREFIX = fetchEnv('BITCOIN_CHAIN') === 'testnet' ? '/testnet' : '/mainnet'
const network = fetchEnv('BITCOIN_CHAIN') === 'testnet' ? networks.testnet : networks.bitcoin

interface FeeInfo {
  feerate: number
  blocks: number
}

interface TransactionResponse {
  txid: string
}

class BitcoinClient {
  private static async request<T>(url: string) {
    const result = await fetch(BASE_URL + CHAIN_PREFIX + url)
    return result.json() as Promise<T>
  }

  static async getUTXOs(address: string): Promise<UTXO[]> {
    const utxos: UTXO[] = await this.request(`/address/${address}/?unspent=true&limit=2000`)
    return utxos.filter((utxo) => utxo.mintHeight > 0)
  }

  static getChainFee(blockTarget: number): Promise<FeeInfo> {
    return this.request(`/fee/${blockTarget}`)
  }

  private static selectUTXOs(utxos: UTXO[], requiredSats: number, excludeOrdinal?: UTXO): UTXO[] {
    const candidates = excludeOrdinal
      ? utxos.filter(
          (u) => u.mintTxid !== excludeOrdinal.mintTxid || u.mintIndex !== excludeOrdinal.mintIndex
        )
      : utxos

    return candidates
      .sort((a, b) => b.value - a.value)
      .reduce(
        (selected, utxo) => {
          if (selected.total < requiredSats) {
            selected.utxos.push(utxo)
            selected.total += utxo.value
          }
          return selected
        },
        { utxos: [] as UTXO[], total: 0 }
      ).utxos
  }

  static getTxWithFee(
    utxos: UTXO[],
    walletAddress: string,
    toAddress: string,
    satoshiAmount: number,
    fee: number,
    ordinalUtxo?: UTXO
  ): string {
    const psbt = new Psbt({ network })

    const inputUtxos = this.selectUTXOs(utxos, satoshiAmount + fee, ordinalUtxo)

    let totalInput = 0

    const addInput = (utxo: UTXO) => {
      const isTaproot = utxo.script.startsWith('5120')
      if (isTaproot) {
        const internalKey = Buffer.from(utxo.script.slice(4), 'hex')
        psbt.addInput({
          hash: utxo.mintTxid,
          index: utxo.mintIndex,
          tapInternalKey: internalKey,
          witnessUtxo: {
            script: Buffer.from(utxo.script, 'hex'),
            value: utxo.value,
          },
        })
      } else {
        psbt.addInput({
          hash: utxo.mintTxid,
          index: utxo.mintIndex,
          witnessUtxo: {
            script: Buffer.from(utxo.script, 'hex'),
            value: utxo.value,
          },
        })
      }
    }

    if (ordinalUtxo) {
      addInput(ordinalUtxo)
      psbt.addOutput({ address: toAddress, value: ordinalUtxo.value })
    }

    inputUtxos.forEach((utxo) => {
      addInput(utxo)
      totalInput += utxo.value
    })

    if (!ordinalUtxo) {
      psbt.addOutput({ address: toAddress, value: satoshiAmount })
    }

    const changeAmount = totalInput - satoshiAmount - fee

    if (changeAmount < 0) {
      throw new Error('Not enough BTC for tx with fee')
    }

    psbt.addOutput({ address: walletAddress, value: changeAmount })

    for (let i = 0; i < psbt.inputCount; i++) {
      const input = psbt.data.inputs[i]
      try {
        if (input.tapInternalKey) {
          psbt.data.updateInput(i, {
            tapScriptSig: [
              {
                pubkey: input.tapInternalKey,
                signature: Buffer.alloc(64, 0),
                leafHash: Buffer.alloc(32, 0),
              },
            ],
          })
        } else {
          // For regular inputs
          psbt.data.updateInput(i, {
            partialSig: [
              {
                pubkey: Buffer.alloc(33, 2),
                signature: Buffer.alloc(72, 0),
              },
            ],
          })
        }
      } catch (e) {
        logger.debug('Eror updating btc inputs', e)
      }
    }

    const taprootInputs = []
    for (let i = 0; i < psbt.inputCount; i++) {
      if (psbt.data.inputs[i].tapInternalKey) {
        taprootInputs.push(i)
      }
    }

    // Finalize only non-Taproot inputs
    for (let i = 0; i < psbt.inputCount; i++) {
      if (!taprootInputs.includes(i)) {
        try {
          psbt.finalizeInput(i)
        } catch (e) {
          logger.debug(`Error finalizing input ${i}:`, e)
        }
      }
    }

    // For Taproot inputs, manually set witness data for size estimation
    for (const i of taprootInputs) {
      try {
        const witnessItems = [
          Buffer.alloc(64, 1), // Dummy signature of correct length
        ]

        // Create a witness script
        let witnessScript = Buffer.allocUnsafe(1)
        witnessScript.writeUInt8(witnessItems.length, 0)

        witnessItems.forEach((item) => {
          const itemLen = Buffer.allocUnsafe(1)
          itemLen.writeUInt8(item.length, 0)
          witnessScript = Buffer.concat([witnessScript, itemLen, item])
        })

        // Update the input with our manually created witness script
        psbt.data.updateInput(i, {
          finalScriptWitness: witnessScript,
        })
      } catch (e) {
        logger.debug(`Error handling Taproot input ${i}:`, e)
      }
    }

    return psbt.extractTransaction(true).toHex()
  }

  static estimateFee(
    utxos: UTXO[],
    userOrdinals: BTCNFT[],
    walletAddress: string,
    toAddress: string,
    amount: number,
    feePerByte: number,
    ordinal?: BTCNFT
  ) {
    let totalFee = 0

    const filteredUtxos = utxos.filter(
      (utxo) =>
        !userOrdinals.some(
          (ordinal) => utxo.mintTxid === ordinal.txId && utxo.mintIndex === ordinal.mintIndex
        )
    )

    const ordinalUtxo = ordinal
      ? utxos.find((u) => u.mintTxid === ordinal.txId && u.mintIndex === ordinal.mintIndex)
      : undefined

    while (true) {
      try {
        const transaction = this.getTxWithFee(
          filteredUtxos,
          walletAddress,
          toAddress,
          amount,
          totalFee,
          ordinalUtxo
        )
        const transactionSize = fromHex(transaction).byteLength

        if ((transactionSize * feePerByte) / 1000 == totalFee) {
          return totalFee
        } else {
          totalFee = (transactionSize * feePerByte) / 1000
        }
      } catch (e) {
        if ((e as Error).message !== 'Not enough BTC for tx with fee') {
          logger.debug('BTC estimateFee error', { originalError: e })
        }

        return null
      }
    }
  }

  static async publishTransaction(rawTx: string): Promise<TransactionResponse> {
    const handleError = (data: any) => {
      logger.error('Failed to publish BTC transaction', data)

      throw new Error('Failed to publish transaction')
    }

    let result
    try {
      result = await fetch(BASE_URL + CHAIN_PREFIX + '/tx/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rawTx }),
      })
    } catch (e) {
      return handleError({ originalError: e })
    }

    if (result.ok) {
      return result.json()
    } else {
      return handleError({ responseBody: result.body })
    }
  }
}

export default BitcoinClient
